import { selectedCustomer } from '@app/ngrx-customer/store';
import { createSelector } from '@ngrx/store';
import {
  BROWSE_RECENTLY_VIEWED_EXPERIENCE,
  BROWSE_TOP_SELLER_EXPERIENCE,
} from '@shared/constants/merchandising-constants';
import { MerchandisingProductViewModel } from '@shared/models/merchandising-product-view.model';
import {
  LoadingState,
  selectMerchandisingResponseById,
  selectMerchandisingResponseByIds,
} from '@usf/ngrx-product';
import { productWithAlternativesSelector } from './product.selectors';
import { Product } from '@shared/models/product.model';
import { Customer } from '@usf/customer-types';
import { ProductFilter } from '@shared/helpers/product-filter.helpers';
import { ProductFilteringEnum } from '@shared/constants/product-filtering.enum';
import { selectAllFeaturesEnabled } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { isProprietaryProduct } from '@product-detail/utils/product-summary.utils';

export const selectMerchandisingViewModel = (experience: string) =>
  createSelector(
    selectedCustomer,
    selectMerchandisingResponseById(experience),
    productWithAlternativesSelector,
    selectAllFeaturesEnabled([FEATURES.split_dynamic_proprietary_ui.name]),
    (
      selectedCustomer,
      merchandisingState,
      products,
      isProprietaryUIEnabled,
    ): MerchandisingProductViewModel[] => {
      const productViewModels: MerchandisingProductViewModel[] = [];

      if (
        merchandisingState?.products?.every(
          product => products.get(product?.productNumber) !== undefined,
        )
      ) {
        merchandisingState?.products?.forEach(merchandisingProduct => {
          const product = products?.get(merchandisingProduct?.productNumber);

          if (
            !!product &&
            applyProductFilter(
              product,
              selectedCustomer,
              experience,
              isProprietaryUIEnabled,
            )
          ) {
            if (!!product?.summary?.properties) {
              product.summary.properties = new Set([
                ...product?.summary?.properties,
                ...product?.summary?.additionalProperties,
              ]);
            }
            const productInfo = {
              ...product,
              selectedCustomer,
              recToken: merchandisingProduct?.recToken,
            } as MerchandisingProductViewModel;
            productViewModels.push(productInfo);
          }
        });
      }
      return productViewModels;
    },
  );

export const selectMerchandisingProductNumbers = (experience: string) =>
  createSelector(
    selectMerchandisingResponseById(experience),
    (merchandisingState): number[] => {
      if (
        [
          LoadingState.loaded,
          LoadingState.error,
          LoadingState.notFound,
        ].includes(merchandisingState?.loadingState)
      ) {
        return !!merchandisingState?.products
          ? merchandisingState.products.map(product => product?.productNumber)
          : [];
      } else {
        return null;
      }
    },
  );

export const selectMerchandisingMultipleExperiencesProductNumbers =
  experiences =>
    createSelector(
      selectMerchandisingResponseByIds(experiences),
      (merchandisingStates): number[][] => {
        if (
          merchandisingStates.every(merchandisingState => {
            return [
              LoadingState.loaded,
              LoadingState.error,
              LoadingState.notFound,
            ].includes(merchandisingState?.loadingState);
          })
        ) {
          return merchandisingStates.map(merchandisingState => {
            return !!merchandisingState?.products
              ? merchandisingState.products.map(
                  product => product?.productNumber,
                )
              : [];
          });
        } else {
          return null;
        }
      },
    );
function applyProductFilter(
  product: Product,
  selectedCustomer: Customer,
  experience: string,
  isProprietaryUIEnabled: boolean,
) {
  switch (experience) {
    case BROWSE_TOP_SELLER_EXPERIENCE:
      if (isProprietaryProduct(product, isProprietaryUIEnabled)) {
        return false;
      }
      return ProductFilter.applyFilter(
        product,
        selectedCustomer,
        ProductFilteringEnum.TOP_SELLERS,
      );
    case BROWSE_RECENTLY_VIEWED_EXPERIENCE:
      return true;
    default:
      if (
        isProprietaryUIEnabled &&
        isProprietaryProduct(product, isProprietaryUIEnabled)
      ) {
        return false;
      }
      return true;
  }
}
